import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Input, Button, Switch, Table, Space, Tag, App, theme } from 'antd';
import { SaveOutlined, ReloadOutlined, DeleteOutlined, PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createAuthApi } from '@zero/auth-admin-ui';
import { invalidateAppConfigCache } from '@zero/auth-client';

// Preset background — admin click chip để fill vào field LoginBackground.
// `darkText: true` cho preset tone sáng (chữ label phải đen mới đọc được).
const BG_PRESETS = [
  { label: 'Marine',   value: 'linear-gradient(135deg, #003972 0%, #0E7BC4 100%)' },
  { label: 'Ocean',    value: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' },
  { label: 'Sunset',   value: 'linear-gradient(135deg, #ff7e5f 0%, #feb47b 100%)', darkText: true },
  { label: 'Aurora',   value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { label: 'Forest',   value: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)' },
  { label: 'Charcoal', value: 'linear-gradient(135deg, #232526 0%, #414345 100%)' },
  { label: 'Mint',     value: 'linear-gradient(135deg, #ddd6f3 0%, #faaca8 100%)', darkText: true },
  { label: 'Navy',     value: '#003972' },
  { label: 'Gray',     value: '#f0f2f5', darkText: true },
  { label: 'White',    value: '#ffffff', darkText: true },
];

/**
 * BrandingPage — admin chỉnh các key Branding.* trong Auth_AppConfig.
 * Sau khi save, invalidate cache client để Sidebar/LoginForm reload tên + subtitle.
 *
 * Bảng phía dưới list toàn bộ entry config (cả non-public) để admin thêm/sửa key tuỳ ý.
 */
export function BrandingPage({ axios }) {
  const { token } = theme.useToken();
  const api = useMemo(() => createAuthApi(axios), [axios]);
  const { t } = useTranslation();
  const { modal, message } = App.useApp();
  const [form]  = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.listAppConfig();
      const list = r.data?.Data ?? [];
      setRows(list);
      // Đổ Branding.* vào form
      const lookup = (key) => list.find((e) => e.ConfigKey === key)?.ConfigValue ?? '';
      form.setFieldsValue({
        BrandName:         lookup('Branding.BrandName'),
        BrandSubtitle:     lookup('Branding.BrandSubtitle'),
        LoginTitle:        lookup('Branding.LoginTitle'),
        LogoURL:           lookup('Branding.LogoURL'),
        FaviconURL:        lookup('Branding.FaviconURL'),
        LoginLogoURL:      lookup('Branding.LoginLogoURL'),
        LoginBackground:   lookup('Branding.LoginBackground'),
        LoginWelcomeText:  lookup('Branding.LoginWelcomeText'),
        LoginFooterText:   lookup('Branding.LoginFooterText'),
      });
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const saveBranding = async (values) => {
    setSaving(true);
    try {
      const updates = [
        ['Branding.BrandName',        values.BrandName        || null],
        ['Branding.BrandSubtitle',    values.BrandSubtitle    || null],
        ['Branding.LoginTitle',       values.LoginTitle       || null],
        ['Branding.LogoURL',          values.LogoURL          || null],
        ['Branding.FaviconURL',       values.FaviconURL       || null],
        ['Branding.LoginLogoURL',     values.LoginLogoURL     || null],
        ['Branding.LoginBackground',  values.LoginBackground  || null],
        ['Branding.LoginWelcomeText', values.LoginWelcomeText || null],
        ['Branding.LoginFooterText',  values.LoginFooterText  || null],
      ];
      for (const [key, value] of updates) {
        await api.upsertAppConfig(key, {
          ConfigKey: key, ConfigValue: value, ValueType: 'String', IsPublic: true,
        });
      }
      invalidateAppConfigCache();   // sidebar + login sẽ refetch
      message.success(t('common.savedAt') + ' ' + new Date().toLocaleTimeString());
      await refresh();
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (key) => {
    try {
      await api.deleteAppConfig(key);
      invalidateAppConfigCache();
      message.success('Deleted');
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    }
  };

  const columns = [
    { title: 'Key',       dataIndex: 'ConfigKey',   width: 240,
      render: (v) => <Tag color="processing" style={{ fontFamily: 'Consolas, monospace' }}>{v}</Tag> },
    { title: 'Value',     dataIndex: 'ConfigValue', ellipsis: true,
      render: (v) => v ?? <span style={{ opacity: 0.4 }}>(null)</span> },
    { title: 'Type',      dataIndex: 'ValueType',   width: 90 },
    { title: 'Public',    dataIndex: 'IsPublic',    width: 80, align: 'center',
      render: (v) => v ? <Tag color="green">public</Tag> : <Tag>private</Tag> },
    { title: 'Updated',   dataIndex: 'UpdatedAtUTC',width: 160,
      render: (v) => v ? new Date(v).toLocaleString() : '-' },
    { title: t('common.actions'), key: 'actions', width: 100,
      render: (_, r) => (
        <Button danger size="small" icon={<DeleteOutlined />}
                onClick={() => modal.confirm({
                  title:    `Delete config entry`,
                  content:  `Key: ${r.ConfigKey}`,
                  icon:     <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
                  okText:   t('common.delete'),
                  okButtonProps: { danger: true },
                  cancelText: t('common.cancel'),
                  onOk:     () => deleteEntry(r.ConfigKey),
                })} />
      ),
    },
  ];

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Card size="small"
            extra={<Button icon={<ReloadOutlined />} size="small" onClick={refresh}>Reload</Button>}>
        <Form form={form} layout="vertical" onFinish={saveBranding} disabled={loading} autoComplete="off">
          <Form.Item name="BrandName" label="Brand name (sidebar header)" rules={[{ required: true }]}>
            <Input placeholder="ZERO" />
          </Form.Item>
          <Form.Item name="BrandSubtitle" label="Brand subtitle (under brand name)">
            <Input placeholder="Reusable authentication & authorization module" />
          </Form.Item>
          <Form.Item name="LoginTitle" label="Login page title (optional, fallback = brand name)">
            <Input />
          </Form.Item>
          <Form.Item name="LogoURL" label="Logo URL — sidebar (optional)">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="FaviconURL" label="Favicon URL (optional)">
            <Input placeholder="https://..." />
          </Form.Item>

          <div style={{ borderTop: '1px solid #e8e8e8', margin: '12px 0 8px', paddingTop: 8 }}>
            <strong style={{ fontSize: 13 }}>Login page customization</strong>
          </div>

          <Form.Item name="LoginLogoURL" label="Login logo URL — ảnh trên trang login (optional)">
            <Input placeholder="https://customer.cdn/logo-login.png" />
          </Form.Item>
          <Form.Item
            name="LoginBackground"
            label="Login background — URL ảnh hoặc CSS color/gradient"
            extra={
              <div style={{ marginTop: 6 }}>
                <div style={{ fontSize: 11, color: token.colorTextSecondary, marginBottom: 4 }}>
                  Mẫu sẵn (click để áp dụng):
                </div>
                <Space wrap size={[6, 6]}>
                  {BG_PRESETS.map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => form.setFieldValue('LoginBackground', p.value)}
                      title={p.value}
                      style={{
                        width: 64, height: 28, padding: 0, border: `1px solid ${token.colorBorder}`,
                        borderRadius: 4, cursor: 'pointer', overflow: 'hidden', position: 'relative',
                        background: p.value,
                      }}
                    >
                      <span style={{
                        position: 'absolute', inset: 0, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 600,
                        color: p.darkText ? '#000' : '#fff',
                        textShadow: p.darkText ? 'none' : '0 1px 2px rgba(0,0,0,0.4)',
                      }}>{p.label}</span>
                    </button>
                  ))}
                  <Button size="small" type="link" onClick={() => form.setFieldValue('LoginBackground', '')}>Xóa</Button>
                </Space>
              </div>
            }
          >
            <Input placeholder="linear-gradient(135deg, #003F7F 0%, #0E7BC4 100%)" />
          </Form.Item>
          <Form.Item
            name="LoginWelcomeText"
            label="Login welcome text — đoạn chào dưới brand"
          >
            <Input.TextArea rows={2} placeholder="Welcome to Marine PMS — Zero" />
          </Form.Item>
          <Form.Item
            name="LoginFooterText"
            label="Login footer text — copyright, links, ..."
          >
            <Input.TextArea rows={2} placeholder="© 2026 Zero. v1.0 — Need help? support@..." />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
              Save branding
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card size="small" title="All config entries" extra={<span style={{ opacity: 0.6 }}>read-only list</span>}>
        <Table
          dataSource={rows}
          columns={columns}
          rowKey="ConfigKey"
          loading={loading}
          size="small"
          bordered
          pagination={false}
          scroll={{ y: 'calc(100vh - 600px)' }}
        />
      </Card>
    </Space>
  );
}
